import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Checkbox } from "antd";
import UserAgreementModal from "../modals/UserAgreementModal";
import PrivacyPolicyModal from "../modals/PrivacyPolicyModal";

interface AgreementLinksProps {
  /** 是否显示复选框 */
  showCheckbox?: boolean;
  /** 复选框的值 */
  checked?: boolean;
  /** 复选框变化回调 */
  onCheckboxChange?: (checked: boolean) => void;
  /** 自定义前缀文本 */
  prefixText?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 链接颜色主题 */
  colors?: {
    primary: string;
    primaryDark: string;
  };
  /** Form.Item 的 value 属性 */
  value?: boolean;
  /** Form.Item 的 onChange 回调 */
  onChange?: (checked: boolean) => void;
}

export const AgreementLinks: React.FC<AgreementLinksProps> = ({
  showCheckbox = true,
  checked = false,
  onCheckboxChange,
  prefixText,
  style,
  colors = {
    primary: "#21C4B8",
    primaryDark: "#4BA67A",
  },
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  // Set default prefixText using t() inside the component if not provided
  const effectivePrefixText = prefixText || t("common.links.read_and_agree");

  const [userAgreementOpen, setUserAgreementOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);

  // 优先使用 Form.Item 传递的 value 和 onChange，否则使用组件自己的 props
  const checkboxValue = value !== undefined ? value : checked;
  const handleCheckboxChange = (newChecked: boolean) => {
    if (onChange) {
      onChange(newChecked);
    } else if (onCheckboxChange) {
      onCheckboxChange(newChecked);
    }
  };

  const linkStyle = {
    color: colors.primary,
    cursor: "pointer",
    textDecoration: "underline" as const,
    fontWeight: 500,
    transition: "all 0.2s ease",
  };

  return (
    <>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          flexWrap: "wrap",
          lineHeight: "1.6",
          ...style,
        }}
      >
        {showCheckbox && (
          <Checkbox
            checked={checkboxValue}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
            style={{ marginRight: "4px" }}
          />
        )}
        <span style={{ marginRight: "2px" }}>{effectivePrefixText}</span>
        <span
          style={{
            ...linkStyle,
            margin: "0 2px",
          }}
          onClick={() => setUserAgreementOpen(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.primaryDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.primary;
          }}
        >
          {t("common.links.user_agreement")}
        </span>
        <span style={{ margin: "0 2px" }}>{t("common.links.and")}</span>
        <span
          style={{
            ...linkStyle,
            margin: "0 2px",
          }}
          onClick={() => setPrivacyPolicyOpen(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.primaryDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.primary;
          }}
        >
          {t("common.links.privacy_policy")}
        </span>
      </div>

      {/* 弹窗组件 */}
      <UserAgreementModal
        open={userAgreementOpen}
        onClose={() => setUserAgreementOpen(false)}
      />
      <PrivacyPolicyModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
      />
    </>
  );
};

export default AgreementLinks;
